import {
  localBuyNowOrderKey,
  localIsBuyNowKey,
  localOrderIdKey,
  localOrderKey,
  setGlobalState,
  versionCookie
} from '@libs/client'
import * as immutable from 'object-path-immutable'
import { AbandonedCart, CartLegacyService } from '@libs/client/order/services'
import { store_type } from '@libs/common/models/constant'
import Loading from '@ui/components/shared/Loading'
import { useNotiContext } from '@ui/components/shared/Notification'
import OrderNotFound from '@ui/components/shared/OrderNotFound'
import { useRedirect, useSetting } from '@ui/hooks'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { getPageBySlug } from '@ui/services'
import { cartMapping } from '@ui/helpers'
import { setCookie } from '@ui/helpers/getCookie'
import getLogRocket from '@ui/analytics/logRocket'
import { logger } from '@ui/analytics'

interface AbandontedOrderProps extends AbandonedCart {
  pversion?: string
}

const AbandonedOrder: NextPage = () => {
  const router = useRouter()
  const abandonedCode = router.query.code?.toString()

  const [storeSetting, setStore] = useSetting('store')
  const isMultiStore = storeSetting?.general?.type === store_type.MINISTORE
  const [sellpageSetting] = useSetting('sellpage')
  const subpath = router.query.subpath as string
  const key = `pversion${subpath ? `_${subpath}` : ''}`
  const [cart, setCart] = useState<AbandontedOrderProps | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingStore, setIsLoadingStore] = useState(true)
  const [isLoadSuccess, setIsLoadSuccess] = useState(false)
  const { notiDispatch } = useNotiContext()
  const redirect = useRedirect()

  const handleAbandonedOrder = (order: Types.Order, subpath?: string) => {
    if (order.items && order.items?.length > 0) {
      setIsLoadSuccess(true)
      setGlobalState({ [localOrderKey(subpath)]: order })
      setGlobalState({ [localOrderIdKey(subpath)]: order.id })
      setGlobalState({
        [localBuyNowOrderKey(subpath)]: { id: order.id, code: order.code }
      })
      setGlobalState({
        [localIsBuyNowKey(subpath)]: false
      })
      logger.logProductEvent('abd_success')
      redirect(`${subpath ? `/${subpath}` : ''}/checkout`, { keepQuery: false, keepSlug: false })
    } else {
      logger.logProductEvent('abd_error')
      setIsLoadSuccess(false)
      redirect('/', { query: { error: 'offer-expired' }, keepQuery: false, keepSlug: false })
    }
  }

  useEffect(() => {
    getLogRocket().logRocket?.init()
    if (abandonedCode) {
      setIsLoading(true)
      logger.logProductEvent('view_abd')
      CartLegacyService.abandoned({
        code: abandonedCode
      })
        .then((res) => {
          if (res.headers?.['version']) {
            setCookie(
              '_version_',
              versionCookie.includes(res.headers?.['version']) ? res.headers?.['version'] : 'v144',
              100
            )
          }
          if (res.headers?.['pversion']) {
            setCookie(key, res.headers?.['pversion'], 100)
          }
          setCart({
            ...res?.data,
            ...(res.headers?.['pversion'] ? { pversion: res.headers?.['pversion'] } : {})
          })
        })
        .catch((err) => {
          logger.logProductEvent('abd_error')
          setIsLoadSuccess(false)
          const errCode = err?.response?.status
          if (errCode === 404) {
            notiDispatch({
              payload: {
                content: 'Abandoned order not found or already purchased',
                type: 'is-warning'
              },
              type: 'REMOVE_ALL_AND_ADD'
            })
          } else {
            console.error(err, err?.response)
          }
        })
        .finally(() => setIsLoading(false))
    } else {
      setTimeout(() => {
        setIsLoadSuccess(false)
        setIsLoading(false)
      }, 1000)
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (cart) {
        if (isMultiStore) {
          const subpath = cart.page_slug ?? ''
          ;(async function () {
            try {
              setIsLoadingStore(true)
              const page = await getPageBySlug(
                storeSetting?.general.id ?? '',
                subpath,
                cart.pversion
              )
              if (page?.setting) {
                setStore((store: S3Types.store) => {
                  const _store = immutable.set(store, `pages.${subpath}.setting`, page.setting)
                  if (_store?.pages?.[subpath]) {
                    _store.pages[subpath].reviews = page.reviews
                  }
                  return _store
                })
                const order = cartMapping(cart, page?.setting.variants?.items ?? {})
                handleAbandonedOrder(order, subpath)
              } else {
                setIsLoadingStore(false)
              }
            } catch (error) {
              setIsLoadingStore(false)
              notiDispatch({
                payload: { content: 'Can not get sell page', type: 'is-danger' },
                type: 'REMOVE_ALL_AND_ADD'
              })
            }
          })()
        } else {
          setIsLoadingStore(false)
          const order = cartMapping(cart, sellpageSetting?.variants?.items ?? {})
          handleAbandonedOrder(order)
        }
      } else {
        setIsLoadingStore(false)
      }
    }
  }, [isLoading, cart])

  return isLoading || isLoadingStore || isLoadSuccess ? (
    <Loading isFullWidth={true} />
  ) : (
    <OrderNotFound className="page__content" />
  )
}

export default AbandonedOrder
